import * as React from "react";
import { CPLN_HELM_RELEASE_PREFIX, homeLink, linksOf, request } from "../../services/cpln";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { BasePathContext, useBasePath } from "../../reactContexts/basePathContext";
import { Loader } from "../../components/layout/loader";
import { HelmReleaseInstance, ReleaseRevision } from "../marketplace/types/release";
import { decodeRevisionFrom, fetchReleaseItems } from "../marketplace/utils";
import { ReleaseInfo } from "./info";
import { ReleaseRevisions } from "./revisions";
import { Secret } from "../../schema/types/secret/entity";
import { IS_DEPLOYMENT_ENV_PROD } from "../../envVariables";
import { ReleaseUninstall } from "./uninstall";
import { Alert } from "antd";
import { useAppSelector } from "../../store/hooks";
import { selectOrg } from "../../store/contextSlice";
import { HelmReleaseDetailHeader } from "../workload/helmReleaseHeader";
import { ContentLayout } from "../../components/layout/contentLayout";
import { SingleNavMenu } from "../../components/antd/layout/singleNavMenu";
import { SingleNavMenuLink, toLinkPage } from "../identity/useNavMenu";

type RouteParams = "release";
export const HelmReleaseDetail: React.FC = () => {
  const org = useAppSelector(selectOrg);

  const { release: releaseName } = useParams<RouteParams>();

  const basePathUpdated = useBasePath(`/helmrelease/${releaseName}/*`);

  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [release, setRelease] = React.useState<HelmReleaseInstance>(null as any);

  const [items, setItems] = React.useState<any[]>([]);
  const [itemsNotFound, setItemsNotFound] = React.useState<any[]>([]);
  const [revisions, setRevisions] = React.useState<ReleaseRevision[]>([]);

  React.useEffect(() => {
    fetchRelease();
  }, []);

  React.useEffect(() => {
    if (!release) {
      return;
    }
    if (revisions.length < 1) {
      return;
    }

    fetchItems();
  }, [revisions]);

  React.useEffect(() => {
    if (!release) {
      return;
    }

    // TODO Get agent health
    // TODO Get volumeset info
  }, [items]);

  async function fetchRelease() {
    try {
      setIsLoading(true);

      let _items: Secret[] = [];
      let nextLink: string | undefined = undefined;

      const url = homeLink("secret") + "/-query";
      const body = {
        kind: "secret",
        spec: {
          match: "all",
          terms: [
            { op: "~", property: "name", value: `${CPLN_HELM_RELEASE_PREFIX}` },
            { op: "!exists", tag: "cpln/marketplace" },
            { op: "=", tag: "name", value: `${releaseName}` },
          ],
        },
      };
      const { data } = await request({ method: "post", url: url, body: body });
      _items = data.items;
      nextLink = linksOf(data).next;

      while (nextLink) {
        const { data } = await request({ url: nextLink, body: body });
        nextLink = linksOf(data).next;
        _items = _items.concat(data.items);
      }
      const secrets = _items
        .filter((i) => i.name.startsWith(`${CPLN_HELM_RELEASE_PREFIX}`))
        .filter((i) => (i.tags || {}).name === releaseName);
      const promises: Promise<any>[] = secrets.map((s) => {
        const selfLink = linksOf(s).self!;
        return request({ url: selfLink + "/-reveal" });
      });
      const templateName = (secrets[0].tags || {})["cpln/marketplace-template"];
      const revealedSecrets: Secret[] = (await Promise.allSettled(promises))
        .filter((r) => r.status === "fulfilled")
        .map((r) => r.value.data);

      const revisions = revealedSecrets.map(decodeRevisionFrom).filter(Boolean) as ReleaseRevision[];
      setRevisions(revisions);

      const sortedRevisions = revisions.toSorted((a, b) => {
        if (a.version > b.version) return -1;
        if (b.version > a.version) return 1;
        return 0;
      });
      const firstRevision = sortedRevisions[sortedRevisions.length - 1];
      const lastRevision = sortedRevisions[0];

      setRelease({
        kind: "helm-release",
        lastModified: lastRevision.info.lastDeployed,
        created: firstRevision.info.firstDeployed,
        tags: {},
        gvc: "",
        org: org,
        name: releaseName!,
        id: releaseName!,
        template: templateName,
        version: lastRevision.chart.metadata.version,
        revision: lastRevision.version,
      });
      setIsLoading(false);
    } catch (e) {
      let errorMessage = e.response?.data?.error;
      if (!errorMessage) errorMessage = e.message;
      setError(errorMessage);
      setIsLoading(false);
    }
  }

  async function fetchItems() {
    try {
      const revision = revisions.toSorted((a, b) => {
        if (a.version > b.version) return -1;
        if (b.version > a.version) return 1;
        return 0;
      })[0];
      const _itemResults = await fetchReleaseItems(revision.info.resources);
      const _itemsNotFound = _itemResults.filter((_i) => _i.notFound === true);
      const _items = _itemResults.filter((_i) => !_i.notFound);

      const kindOrder = [
        "gvc",
        "workload",
        "volumeset",
        "identity",
        "domain",
        "agent",
        "secret",
        "cloudaccount",
        "group",
        "serviceaccount",
        "policy",
        "auditctx",
      ];

      let _sortedItems: any[] = [];
      for (const kind of kindOrder) {
        _sortedItems = _sortedItems.concat(..._items.filter((i) => i.kind === kind));
      }
      setItems(_sortedItems);
      let _sortedItemsNotFound: any[] = [];
      for (const kind of kindOrder) {
        _sortedItemsNotFound = _sortedItemsNotFound.concat(..._itemsNotFound.filter((i) => i.kind === kind));
      }
      setItemsNotFound(_sortedItemsNotFound);
      setIsLoading(false);
    } catch (e) {
      let errorMessage = e.response?.data?.error;
      if (!errorMessage) errorMessage = e.message;
      setError(errorMessage);
      setIsLoading(false);
    }
  }

  const links: SingleNavMenuLink[] = React.useMemo(() => {
    return [toLinkPage("Info", "/info"), toLinkPage("Revisions", "/revisions"), toLinkPage("Uninstall", "/uninstall")];
  }, []);

  if (error) {
    return (
      <>
        <Alert type={"error"} message="Error" description={`${error}`} />;
        {IS_DEPLOYMENT_ENV_PROD ? null : (
          <div className="flex flex-col gap-4 text-sm">
            <div>Release</div>
            <pre>{JSON.stringify(release, null, 2)}</pre>
            <div>Revisions</div>
            <pre>{JSON.stringify(revisions, null, 2)}</pre>
          </div>
        )}
      </>
    );
  }

  if (!release || isLoading) {
    return <Loader />;
  }

  return (
    <>
      <BasePathContext.Provider value={basePathUpdated}>
        <HelmReleaseDetailHeader release={release} items={items} />
        <ContentLayout
          nav={<SingleNavMenu useDetailHeader={true} links={links} />}
          content={
            <Routes>
              <Route index element={<Navigate to={`${basePathUpdated}/info`} replace />} />
              <Route
                path={`info/*`}
                element={<ReleaseInfo release={release} items={items} itemsNotFound={itemsNotFound} />}
              />
              <Route path={`revisions/*`} element={<ReleaseRevisions release={release} revisions={revisions} />} />
              <Route path={`uninstall/*`} element={<ReleaseUninstall release={release} />} />
            </Routes>
          }
        />
      </BasePathContext.Provider>
    </>
  );
};
